import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useMarkRecordFormDirty } from '../host/recordFormDirty';

const CustomDropdown = ({
  label,
  options = [],
  value,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  required = false,
  style = {},
  width = '100%',
  height = '40px',
  fullWidth = false,
  id,
  menuPortal = false,
  error = false,
  helperText = '',
  allowCustom = false,
  createOptionLabel = 'Use',
  htmlRequired
}) => {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuPosition, setMenuPosition] = useState(null);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const optionsListRef = useRef(null);
  const effectiveRequired = htmlRequired === false
    ? false
    : Boolean(required || (typeof label === 'string' && label.includes('*')));
  const markRecordDirty = useMarkRecordFormDirty();
  const emitChange = useCallback((next) => {
    markRecordDirty();
    onChange?.(next);
  }, [markRecordDirty, onChange]);

  const MENU_PORTAL_MAX_H = 300;

  const updateMenuPosition = () => {
    if (!menuPortal || !inputRef.current) {
      setMenuPosition(null);
      return;
    }
    const rect = inputRef.current.getBoundingClientRect();
    if (rect.width < 1 && rect.height < 1) {
      setMenuPosition(null);
      return;
    }
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const placeAbove =
      spaceBelow < MENU_PORTAL_MAX_H && spaceAbove >= MENU_PORTAL_MAX_H;
    const gap = placeAbove ? 1 : 4;
    if (placeAbove) {
      setMenuPosition({
        left: rect.left,
        width: rect.width,
        placeAbove: true,
        bottom: window.innerHeight - rect.top + gap
      });
    } else {
      setMenuPosition({
        top: rect.bottom + gap,
        left: rect.left,
        width: rect.width,
        placeAbove: false
      });
    }
  };

  useLayoutEffect(() => {
    if (open && menuPortal) {
      updateMenuPosition();
      const onScrollOrResize = () => updateMenuPosition();
      window.addEventListener('scroll', onScrollOrResize, true);
      window.addEventListener('resize', onScrollOrResize);
      return () => {
        window.removeEventListener('scroll', onScrollOrResize, true);
        window.removeEventListener('resize', onScrollOrResize);
      };
    } else if (!open) {
      setMenuPosition(null);
    }
  }, [open, menuPortal]);

  // Stop wheel scroll chaining to the page when the options list is at top/bottom.
  useEffect(() => {
    if (!open) return;
    const el = optionsListRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (e.deltaY === 0) return;
      const { scrollTop, scrollHeight, clientHeight } = el;
      const deltaY = e.deltaY;
      const epsilon = 1;
      const atTop = scrollTop <= epsilon;
      const atBottom = scrollTop + clientHeight >= scrollHeight - epsilon;
      if ((deltaY < 0 && atTop) || (deltaY > 0 && atBottom)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [open, menuPosition]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target) && 
          inputRef.current && !inputRef.current.contains(e.target) &&
          menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
        setSearchTerm('');
      }
    }
    if (open) document.addEventListener('mousedown', handleClick, true);
    return () => document.removeEventListener('mousedown', handleClick, true);
  }, [open]);

  // Filter options based on search term
  const normalizedOptions = Array.isArray(options)
    ? options.map((opt, i) => ({ value: opt?.value ?? i, label: String(opt?.label ?? ''), raw: opt }))
    : [];
  const filteredOptions = normalizedOptions.filter(option => 
    option.label && option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const trimmedSearchTerm = searchTerm.trim();
  const hasExactMatch = normalizedOptions.some(
    (opt) => opt.label.trim().toLowerCase() === trimmedSearchTerm.toLowerCase()
  );
  const canCreateCustom =
    allowCustom && trimmedSearchTerm.length > 0 && !hasExactMatch;

  // Reset highlighted when search changes
  useEffect(() => {
    setHighlighted(-1);
  }, [searchTerm]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    function handleKey(e) {
      if (e.key === 'ArrowDown') {
        setHighlighted(h => Math.min(h + 1, filteredOptions.length - 1));
        e.preventDefault();
      } else if (e.key === 'ArrowUp') {
        setHighlighted(h => Math.max(h - 1, 0));
        e.preventDefault();
      } else if (e.key === 'Enter' && highlighted >= 0) {
        emitChange(filteredOptions[highlighted].value);
        setOpen(false);
        setSearchTerm('');
        e.preventDefault();
      } else if (e.key === 'Enter' && canCreateCustom) {
        emitChange(trimmedSearchTerm);
        setOpen(false);
        setSearchTerm('');
        e.preventDefault();
      } else if (e.key === 'Escape') {
        setOpen(false);
        setSearchTerm('');
        e.preventDefault();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, highlighted, filteredOptions, emitChange, canCreateCustom, trimmedSearchTerm]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (open && menuRef.current && highlighted >= 0) {
      // The menu structure is: menuRef > [search div, options list div]
      // So we need to access the options list div (children[1]) and then its children
      const optionsListDiv = menuRef.current.children[1];
      if (optionsListDiv) {
        const el = optionsListDiv.children[highlighted];
        if (el) el.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [open, highlighted]);

  // Find selected label
  const selected = normalizedOptions.find(opt => String(opt.value) === String(value))
    || (value != null && value !== '' ? { value, label: String(value) } : null);
  const inputId = id || `dropdown-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div ref={ref} className="custom-dropdown" style={{ 
      width: '100%', 
      minWidth: '100%',
      maxWidth: '100%',
      position: 'relative', 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'flex-start',
      flex: '1 1 100%',
      overflow: 'visible',
      ...style
    }}>
      {label && (
        <label htmlFor={inputId} style={{ 
          display: 'block', 
          marginBottom: '8px',
          marginLeft: '10px',
          fontSize: '14px', 
          color: 'var(--color-text)',
          fontWeight: '500',
          textAlign: 'left'
  }}>
          {label}
        </label>
      )}
      <button
        ref={inputRef}
        id={inputId}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label || placeholder}
        aria-required={effectiveRequired}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!disabled) {
            setOpen(o => !o);
          }
        }}
        style={{
          width: '100%',
          minWidth: '100%',
          maxWidth: '100%',
          height,
          minHeight: height,
          padding: '8px 16px',
          borderRadius: 8,
          border: `1px solid ${error ? 'var(--color-error, #d32f2f)' : 'var(--color-dropdown-border)'}`,
          background: disabled ? 'var(--color-bg-alt)' : 'var(--color-bg-input)',
          color: value ? 'var(--color-text)' : 'var(--color-text-secondary)',
          fontSize: '15px',
          fontFamily: 'inherit',
          fontWeight: 400,
          outline: 'none',
          opacity: disabled ? 0.6 : 1,
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'border 0.2s',
          boxSizing: 'border-box',
          flex: '0 1 auto',
          overflow: 'hidden',
        }}
        onFocus={e => (e.target.style.border = error ? '1.5px solid var(--color-error, #d32f2f)' : '1.5px solid var(--color-accent)')}
        onBlur={e => (e.target.style.border = `1px solid ${error ? 'var(--color-error, #d32f2f)' : 'var(--color-dropdown-border)'}`)}
      >
        <span>{selected ? selected.label : <span style={{ color: 'var(--color-text-secondary)' }}>{placeholder}</span>}</span>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-secondary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 8 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {helperText && <div style={{ marginTop: 4, fontSize: '12px', color: 'var(--color-error, #d32f2f)' }}>{helperText}</div>}

      {/* Menu: portal when menuPortal (e.g. filter sections) so it escapes scroll container */}
      {open && (() => {
        if (menuPortal && menuPosition == null) return null;
        const menuStyle = menuPortal && menuPosition != null
          ? {
              position: 'fixed',
              ...(menuPosition.placeAbove
                ? { bottom: menuPosition.bottom, top: 'auto' }
                : { top: menuPosition.top, bottom: 'auto' }),
              left: menuPosition.left,
              width: menuPosition.width,
              minWidth: menuPosition.width,
              background: 'var(--color-bg-alt)',
              color: 'var(--color-text)',
              border: '2px solid var(--color-accent)',
              borderRadius: 8,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              zIndex: 10000,
              overflow: 'hidden',
              fontFamily: 'inherit',
              maxHeight: `${MENU_PORTAL_MAX_H}px`
            }
          : {
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: 4,
              width: '100%',
              minWidth: '100%',
              background: 'var(--color-bg-alt)',
              color: 'var(--color-text)',
              border: '2px solid var(--color-accent)',
              borderRadius: 8,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              zIndex: 10000,
              overflow: 'hidden',
              fontFamily: 'inherit',
              maxHeight: '300px'
            };
        const menuEl = (
          <div
            ref={menuRef}
            role="listbox"
            tabIndex={-1}
            className={menuPortal ? 'custom-dropdown-menu-portal' : undefined}
            style={menuStyle}
          >
          {/* Search Input */}
          <div style={{
            padding: '8px 12px',
            borderBottom: '1px solid var(--color-border)',
            position: 'sticky',
            top: 0,
            background: 'var(--color-bg-alt)',
            zIndex: 1
  }}>
            <input
              type="text"
              placeholder={allowCustom ? 'Search or type custom option' : 'Search...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                minHeight: '18px',
                background: 'var(--color-bg-input)',
                border: '1px solid var(--color-border)',
                borderRadius: '4px',
                padding: '6px 8px',
                color: 'var(--color-text)',
                fontSize: '12px',
                outline: 'none',
                boxSizing: 'border-box'
  }}
              onFocus={(e) => {
                e.target.style.border = '1px solid var(--color-accent)';
              }}
              onBlur={(e) => {
                e.target.style.border = '1px solid var(--color-border)';
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Options List */}
          <div
            ref={optionsListRef}
            style={{
              maxHeight: '180px',
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              touchAction: 'pan-y',
            }}
          >
            {filteredOptions.length === 0 && (
              <div style={{ padding: 12, color: 'var(--color-text-secondary)', textAlign: 'center' }}>
                {searchTerm ? 'No options found' : 'No options'}
              </div>
            )}
            {canCreateCustom && (
              <div
                role="option"
                aria-selected={false}
                onClick={() => {
                  emitChange(trimmedSearchTerm);
                  setOpen(false);
                  setSearchTerm('');
                }}
                style={{
                  padding: '10px 16px',
                  background: 'transparent',
                  color: 'var(--color-text)',
                  cursor: 'pointer',
                  fontWeight: 500,
                  fontSize: 15,
                  borderRadius: 6,
                  margin: '2px 4px',
                  border: '1px dashed var(--color-accent)',
                  textAlign: 'left',
                }}
              >
                {createOptionLabel} "{trimmedSearchTerm}"
              </div>
            )}
             {filteredOptions.map((opt, i) => (
              <div
                key={opt.value}
                role="option"
                aria-selected={value === opt.value}
                onClick={() => { 
                  emitChange(opt.value); 
                  setOpen(false); 
                  setSearchTerm('');
                }}
                onMouseEnter={() => setHighlighted(i)}
                style={{
                  padding: '10px 16px',
                  background: highlighted === i ? 'var(--color-accent2)' : value === opt.value ? 'var(--color-accent)' : 'transparent',
                  color: highlighted === i || value === opt.value ? '#fff' : 'var(--color-text)',
                  cursor: 'pointer',
                  fontWeight: value === opt.value ? 600 : 400,
                  fontSize: 15,
                  borderRadius: 6,
                  margin: '2px 4px',
                  transition: 'background 0.15s',
                  textAlign: 'left',
                }}
              >
                {opt.label}
              </div>
            ))}
          </div>
        </div>
        );
        return menuPortal && typeof document !== 'undefined'
          ? createPortal(menuEl, document.body)
          : menuEl;
      })()}
    </div>
  );
};

export default CustomDropdown;